import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import StatsCard from '../components/StatsCard';
import {
  Users,
  Eye,
  FileText,
  Globe,
  TrendingUp,
  ArrowUpRight,
  Clock,
  Monitor,
  Smartphone,
  Tablet,
} from 'lucide-react';
import '../styles/dashboard.css';

const DashboardPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchRealtime, 30000); // Update realtime every 30s
    return () => clearInterval(interval);
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, analyticsRes, realtimeRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/analytics/summary?period=${period}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/analytics/realtime`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (realtimeRes.ok) setRealtime(await realtimeRes.json());
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRealtime = async () => {
    try {
      const res = await fetch(`${API_URL}/api/analytics/realtime`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setRealtime(await res.json());
    } catch (error) {
      console.error('Failed to fetch realtime data:', error);
    }
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case 'mobile': return Smartphone;
      case 'tablet': return Tablet;
      default: return Monitor;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="dashboard-loading">
          <div className="loading-spinner" />
          <p>Loading dashboard...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="dashboard-page">
        {/* Header with period selector */}
        <div className="dashboard-header">
          <div>
            <h2>Dashboard Overview</h2>
            <p>Monitor your website performance and content</p>
          </div>
          <div className="period-selector">
            {['7d', '30d', '90d'].map((p) => (
              <button
                key={p}
                className={period === p ? 'active' : ''}
                onClick={() => setPeriod(p)}
              >
                {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
        </div>

        {/* Real-time Stats */}
        {realtime && (
          <div className="realtime-banner">
            <div className="realtime-indicator">
              <span className="pulse" />
              <span>Live</span>
            </div>
            <div className="realtime-stats">
              <div className="realtime-stat">
                <Users size={16} />
                <span><strong>{realtime.active_visitors}</strong> active visitors</span>
              </div>
              <div className="realtime-stat">
                <Eye size={16} />
                <span><strong>{realtime.views_last_30min}</strong> views in last 30 min</span>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="stats-grid">
          <StatsCard
            title="Total Page Views"
            value={analytics?.visitor_stats?.total_visits?.toLocaleString() || '0'}
            icon={Eye}
            color="blue"
          />
          <StatsCard
            title="Unique Visitors"
            value={analytics?.visitor_stats?.unique_visitors?.toLocaleString() || '0'}
            icon={Users}
            color="green"
          />
          <StatsCard
            title="Published Pages"
            value={stats?.pages?.published || '0'}
            icon={FileText}
            color="purple"
          />
          <StatsCard
            title="Draft Pages"
            value={stats?.pages?.drafts || '0'}
            icon={Globe}
            color="orange"
          />
        </div>

        {/* Charts and Tables */}
        <div className="dashboard-grid">
          {/* Top Pages */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3>Top Pages</h3>
              <TrendingUp size={18} />
            </div>
            <div className="card-content">
              {analytics?.top_pages?.length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Page</th>
                      <th>Views</th>
                      <th>Visitors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.top_pages.slice(0, 5).map((page, idx) => (
                      <tr key={idx}>
                        <td className="page-cell">
                          <span className="page-path">{page.page_path}</span>
                        </td>
                        <td>{page.views}</td>
                        <td>{page.unique_visitors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="empty-state">No page data yet</p>
              )}
            </div>
          </div>

          {/* Traffic Sources */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3>Traffic Sources</h3>
              <ArrowUpRight size={18} />
            </div>
            <div className="card-content">
              {analytics?.top_referrers?.length > 0 ? (
                <div className="referrer-list">
                  {analytics.top_referrers.slice(0, 5).map((ref, idx) => (
                    <div key={idx} className="referrer-item">
                      <div className="referrer-info">
                        <Globe size={16} />
                        <span>{ref.referrer}</span>
                      </div>
                      <div className="referrer-stats">
                        <span className="referrer-visits">{ref.visits}</span>
                        <span className="referrer-percent">{ref.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-state">No referrer data yet</p>
              )}
            </div>
          </div>

          {/* Devices */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3>Devices</h3>
              <Monitor size={18} />
            </div>
            <div className="card-content">
              {analytics?.devices?.length > 0 ? (
                <div className="device-list">
                  {analytics.devices.map((device, idx) => {
                    const DeviceIcon = getDeviceIcon(device.device_type);
                    return (
                      <div key={idx} className="device-item">
                        <div className="device-info">
                          <DeviceIcon size={20} />
                          <span className="device-type">{device.device_type}</span>
                        </div>
                        <div className="device-bar-container">
                          <div 
                            className="device-bar" 
                            style={{ width: `${device.percentage}%` }}
                          />
                        </div>
                        <span className="device-percent">{device.percentage}%</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="empty-state">No device data yet</p>
              )}
            </div>
          </div>

          {/* Countries */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3>Top Countries</h3>
              <Globe size={18} />
            </div>
            <div className="card-content">
              {analytics?.countries?.length > 0 ? (
                <div className="country-list">
                  {analytics.countries.slice(0, 5).map((country, idx) => (
                    <div key={idx} className="country-item">
                      <span className="country-name">{country.country}</span>
                      <div className="country-stats">
                        <span className="country-visits">{country.visits}</span>
                        <span className="country-percent">{country.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-state">No country data yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Daily Chart */}
        {analytics?.daily_stats?.length > 0 && (
          <div className="dashboard-card full-width">
            <div className="card-header">
              <h3>Daily Visits</h3>
              <Clock size={18} />
            </div>
            <div className="card-content">
              <div className="daily-chart">
                {analytics.daily_stats.map((day, idx) => (
                  <div key={idx} className="chart-bar-container">
                    <div 
                      className="chart-bar"
                      style={{ 
                        height: `${Math.max(10, (day.visits / Math.max(...analytics.daily_stats.map(d => d.visits))) * 100)}%` 
                      }}
                      title={`${day.visits} visits`}
                    />
                    <span className="chart-label">
                      {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default DashboardPage;
